import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Checkbox,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Verified as VerifiedIcon,
  AccountTree as PersonIcon,
  LinkOff as UnlinkIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { UserListItem } from '../../types/user';

/**
 * Sort configuration
 */
type Order = 'asc' | 'desc';
type OrderBy = 'displayName' | 'email' | 'role' | 'status' | 'createdAt' | 'lastLoginAt';

/**
 * Props for UserTable component
 */
interface UserTableProps {
  users: UserListItem[];
  onEdit?: (userId: string) => void;
  onDelete?: (userId: string) => void;
  onSuspend?: (userId: string) => void;
  onApprove?: (userId: string) => void;
  onUnlink?: (userId: string) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  selectable?: boolean;
  showActions?: boolean;
  dense?: boolean;
}

/**
 * UserTable Component
 * Displays users in a sortable, paginated table with bulk selection
 *
 * Features:
 * - Sortable columns (name, email, role, status, dates)
 * - Pagination
 * - Bulk selection with checkboxes
 * - Quick action buttons (edit, delete, suspend, approve)
 * - Role and status badges
 * - Email verification indicator
 * - Responsive design
 */
const UserTable: React.FC<UserTableProps> = ({
  users,
  onEdit,
  onDelete,
  onSuspend,
  onApprove,
  onUnlink,
  onSelectionChange,
  selectable = false,
  showActions = true,
  dense = false,
}) => {
  const navigate = useNavigate();

  // State
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('displayName');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /**
   * Handle sort request
   */
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Handle select all click
   */
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = users.map((user) => user.uid);
      setSelected(newSelected);
      if (onSelectionChange) {
        onSelectionChange(newSelected);
      }
      return;
    }
    setSelected([]);
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  /**
   * Handle click on checkbox
   */
  const handleClick = (userId: string) => {
    const selectedIndex = selected.indexOf(userId);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, userId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
    if (onSelectionChange) {
      onSelectionChange(newSelected);
    }
  };

  /**
   * Handle page change
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /**
   * Handle rows per page change
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Handle row click
   */
  const handleRowClick = (userId: string) => {
    if (!selectable) {
      navigate(`/admin/users/${userId}`);
    }
  };

  /**
   * Get role badge color
   */
  const getRoleColor = (
    role: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'editor':
        return 'warning';
      case 'contributor':
        return 'info';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  /**
   * Get user status based on isApproved
   */
  const getUserStatus = (user: UserListItem): string => {
    if (!user.isApproved) return 'pending';
    return 'approved';
  };

  /**
   * Get status color
   */
  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Get user initials
   */
  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Sort comparator
   */
  const getComparator = (order: Order, orderBy: OrderBy) => {
    return (a: UserListItem, b: UserListItem) => {
      let aValue: any;
      let bValue: any;

      // Handle special case for status (computed field)
      if (orderBy === 'status') {
        aValue = getUserStatus(a);
        bValue = getUserStatus(b);
      } else {
        aValue = a[orderBy];
        bValue = b[orderBy];
      }

      // Handle dates
      if (aValue instanceof Date) aValue = aValue.getTime();
      if (bValue instanceof Date) bValue = bValue.getTime();

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Compare
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    };
  };

  // Sort and paginate users
  const sortedUsers = [...users].sort(getComparator(order, orderBy));
  const paginatedUsers = sortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const isSelected = (userId: string) => selected.indexOf(userId) !== -1;

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <Table size={dense ? 'small' : 'medium'} aria-label="user table">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < users.length}
                    checked={users.length > 0 && selected.length === users.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
              )}
              <TableCell>User</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? order : 'asc'}
                  onClick={() => handleRequestSort('role')}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Linked Person</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'lastLoginAt'}
                  direction={orderBy === 'lastLoginAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('lastLoginAt')}
                >
                  Last Login
                </TableSortLabel>
              </TableCell>
              {showActions && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={selectable ? 8 : 7}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => {
                const isItemSelected = isSelected(user.uid);
                return (
                  <TableRow
                    key={user.uid}
                    hover
                    onClick={() => handleRowClick(user.uid)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    sx={{ cursor: selectable ? 'default' : 'pointer' }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClick(user.uid);
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: `${getRoleColor(user.role)}.main`,
                            fontSize: '0.875rem',
                          }}
                        >
                          {getInitials(user.displayName)}
                        </Avatar>
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                              {user.displayName}
                            </Typography>
                            {user.isEmailVerified && (
                              <Tooltip title="Email verified">
                                <VerifiedIcon sx={{ fontSize: 16 }} color="primary" />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        size="small"
                        color={getRoleColor(user.role)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getUserStatus(user).charAt(0).toUpperCase() + getUserStatus(user).slice(1)}
                        size="small"
                        color={getStatusColor(getUserStatus(user))}
                      />
                    </TableCell>
                    <TableCell>
                      {user.personId ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon sx={{ fontSize: 16 }} color="primary" />
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/people/${user.personId}`);
                            }}
                          >
                            View Profile
                          </Typography>
                          {onUnlink && (
                            <Tooltip title="Unlink user from person">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onUnlink(user.uid);
                                }}
                              >
                                <UnlinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.lastLoginAt
                          ? format(user.lastLoginAt, 'MMM d, yyyy')
                          : 'Never'}
                      </Typography>
                    </TableCell>
                    {showActions && (
                      <TableCell align="right">
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          {getUserStatus(user) === 'pending' && onApprove && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onApprove(user.uid);
                                }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onEdit && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(user.uid);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onSuspend && getUserStatus(user) === 'approved' && (
                            <Tooltip title="Suspend">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSuspend(user.uid);
                                }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(user.uid);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default UserTable;
